import React, { useRef } from 'react';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
} from '@heroicons/react/24/outline';

interface TextStyleToolbarProps {
  onStyleApply: (style: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
}

const FONTS = [
  { label: 'Default (System)', value: 'system' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Courier', value: '"Courier New", monospace' },
];

const FONT_SIZES = [
  { label: 'Small (12px)', value: '12px' },
  { label: 'Normal (14px)', value: '14px' },
  { label: 'Medium (16px)', value: '16px' },
  { label: 'Large (18px)', value: '18px' },
  { label: 'Extra Large (20px)', value: '20px' },
  { label: 'Huge (24px)', value: '24px' },
];

const TEXT_ALIGNMENTS = [
  { label: 'Left', value: 'left', symbol: '◄' },
  { label: 'Center', value: 'center', symbol: '⊙' },
  { label: 'Right', value: 'right', symbol: '►' },
  { label: 'Justify', value: 'justify', symbol: '☰' },
];

export const TextStyleToolbar: React.FC<TextStyleToolbarProps> = ({
  onStyleApply,
  textareaRef,
}) => {
  const fontSelectRef = useRef<HTMLSelectElement>(null);
  const sizeSelectRef = useRef<HTMLSelectElement>(null);

  const insertStyle = (html: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    let styledText = html;

    if (selectedText) {
      // Wrap selected text with the style
      styledText = html.replace('TEXT', selectedText);
    }

    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);
    const newMessage = beforeText + styledText + afterText;

    onStyleApply(newMessage);

    // Reset selection and focus
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + styledText.length,
        start + styledText.length
      );
    }, 0);
  };

  const handleBold = () => {
    insertStyle('<b>TEXT</b>');
  };

  const handleItalic = () => {
    insertStyle('<i>TEXT</i>');
  };

  const handleUnderline = () => {
    insertStyle('<u>TEXT</u>');
  };

  const handleFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const fontFamily = e.target.value;
    if (!fontFamily) return;

    if (fontFamily === 'system') {
      insertStyle('TEXT');
    } else {
      const styleAttr =
        fontFamily.includes('"') || fontFamily.includes("'")
          ? `font-family: ${fontFamily}`
          : `font-family: ${fontFamily}`;
      insertStyle(`<span style="${styleAttr}">TEXT</span>`);
    }

    e.target.value = '';
  };

  const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const fontSize = e.target.value;
    if (!fontSize) return;

    insertStyle(`<span style="font-size: ${fontSize}">TEXT</span>`);
    e.target.value = '';
  };

  const handleAlignment = (alignment: string) => {
    insertStyle(`<div style="text-align: ${alignment}">TEXT</div>`);
  };

  return (
    <div className="flex flex-wrap gap-2 p-3 bg-slate-800/30 border border-slate-700/40 rounded-lg mb-3">
      {/* Bold, Italic, Underline */}
      <div className="flex gap-1 border-r border-slate-700/40 pr-2">
        <button
          type="button"
          onClick={handleBold}
          title="Bold (Ctrl+B)"
          className="p-2 bg-slate-700/50 hover:bg-slate-600 text-slate-300 rounded transition-colors"
        >
          <BoldIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={handleItalic}
          title="Italic (Ctrl+I)"
          className="p-2 bg-slate-700/50 hover:bg-slate-600 text-slate-300 rounded transition-colors"
        >
          <ItalicIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={handleUnderline}
          title="Underline (Ctrl+U)"
          className="p-2 bg-slate-700/50 hover:bg-slate-600 text-slate-300 rounded transition-colors"
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Font Family */}
      <select
        ref={fontSelectRef}
        onChange={handleFontChange}
        defaultValue=""
        title="Change font family"
        className="px-3 py-2 bg-slate-700/50 hover:bg-slate-600 text-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500"
      >
        <option value="">Font...</option>
        {FONTS.map((font) => (
          <option key={font.value} value={font.value}>
            {font.label}
          </option>
        ))}
      </select>

      {/* Font Size */}
      <select
        ref={sizeSelectRef}
        onChange={handleSizeChange}
        defaultValue=""
        title="Change font size"
        className="px-3 py-2 bg-slate-700/50 hover:bg-slate-600 text-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500"
      >
        <option value="">Size...</option>
        {FONT_SIZES.map((size) => (
          <option key={size.value} value={size.value}>
            {size.label}
          </option>
        ))}
      </select>

      {/* Text Alignment */}
      <div className="flex gap-1 border-l border-slate-700/40 pl-2">
        {TEXT_ALIGNMENTS.map((align) => (
          <button
            key={align.value}
            type="button"
            onClick={() => handleAlignment(align.value)}
            title={`Align ${align.label}`}
            className="p-2 bg-slate-700/50 hover:bg-slate-600 text-slate-300 rounded transition-colors text-sm font-medium"
          >
            {align.symbol}
          </button>
        ))}
      </div>
    </div>
  );
};
